'use client'

import { useEffect, useRef, useState } from 'react'
import { usePathname } from 'next/navigation'

type Phase = 'idle' | 'running' | 'done'

export function NavigationProgress() {
  const pathname = usePathname()
  const [phase, setPhase] = useState<Phase>('idle')
  const barRef = useRef<HTMLDivElement>(null)
  const progressRef = useRef(0)
  const startPathnameRef = useRef(pathname)

  // Fill animation while navigation is in progress
  useEffect(() => {
    if (phase !== 'running') return
    progressRef.current = 0
    let raf: number

    const fill = () => {
      const remaining = 85 - progressRef.current
      progressRef.current += remaining * 0.025 + 0.05
      if (barRef.current) {
        barRef.current.style.width = `${progressRef.current}%`
      }
      if (progressRef.current < 84.9) {
        raf = requestAnimationFrame(fill)
      }
    }
    raf = requestAnimationFrame(fill)
    return () => cancelAnimationFrame(raf)
  }, [phase])

  // Complete bar when destination pathname is reached
  useEffect(() => {
    if (phase !== 'running') return
    if (pathname === startPathnameRef.current) return
    if (barRef.current) {
      barRef.current.style.transition = 'width 0.15s ease-out'
      barRef.current.style.width = '100%'
    }
    setPhase('done')
  }, [pathname, phase])

  // Hide bar after completion animation finishes
  useEffect(() => {
    if (phase !== 'done') return
    const timer = setTimeout(() => {
      setPhase('idle')
      progressRef.current = 0
    }, 600)
    return () => clearTimeout(timer)
  }, [phase])

  // Detect navigation start via link clicks
  useEffect(() => {
    const onClick = (e: MouseEvent) => {
      const a = (e.target as Element).closest('a[href]') as HTMLAnchorElement | null
      if (!a) return
      const href = a.getAttribute('href') ?? ''
      if (!href || href === pathname) return
      if (/^(https?:|\/\/|#|mailto:|tel:)/.test(href)) return
      startPathnameRef.current = pathname
      setPhase('running')
    }
    document.addEventListener('click', onClick)
    return () => document.removeEventListener('click', onClick)
  }, [pathname])

  if (phase === 'idle') return null

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        zIndex: 9999,
        height: 3,
        pointerEvents: 'none',
        opacity: phase === 'done' ? 0 : 1,
        transition: phase === 'done' ? 'opacity 0.35s ease 0.15s' : undefined,
      }}
    >
      <div
        ref={barRef}
        style={{
          height: '100%',
          width: '0%',
          background: 'linear-gradient(90deg, #0191d3, #03a8f5)',
          boxShadow: '0 0 10px rgba(3, 168, 245, 0.6)',
        }}
      />
    </div>
  )
}
